import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
} from 'react'
import gsap from 'gsap'
import { Plus } from 'lucide-react'
import { EASE_INOUT, prefersReducedMotion } from '../../animations/gsapAnimations'

export type AccordionItem = {
  id: string
  question: string
  answer: ReactNode
  /** Spread onto the row. What lets a CMS section hand each question its
      colour overrides and the hooks the console selects it by. */
  props?: HTMLAttributes<HTMLDivElement>
}

type AccordionProps = {
  items: readonly AccordionItem[]
  /** Leave one panel open on first paint — usually the first question. */
  defaultOpenId?: string
  /** Accordion behaviour by default: opening one closes the others. */
  allowMultiple?: boolean
  className?: string
}

type RowProps = {
  item: AccordionItem
  open: boolean
  onToggle: () => void
}

function AccordionRow({ item, open, onToggle }: RowProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const firstRender = useRef(true)
  const id = useId()

  /* GSAP measures the natural height for us, so the panel animates from 0 to
     its real content height without hard-coding a max-height guess. */
  useLayoutEffect(() => {
    const panel = panelRef.current
    if (!panel) return

    if (firstRender.current || prefersReducedMotion()) {
      firstRender.current = false
      gsap.set(panel, { height: open ? 'auto' : 0, opacity: open ? 1 : 0 })
      return
    }

    const tween = gsap.to(panel, {
      height: open ? 'auto' : 0,
      opacity: open ? 1 : 0,
      duration: 0.5,
      ease: EASE_INOUT,
    })

    return () => {
      tween.kill()
    }
  }, [open])

  return (
    <div {...item.props} className="border-b border-line last:border-0">
      <h3>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={`${id}-panel`}
          id={`${id}-button`}
          className="group flex w-full items-center justify-between gap-6 py-6 text-left"
        >
          <span
            className={`font-display text-[17px] font-medium transition-colors duration-200 sm:text-[19px] ${
              open ? 'text-platinum' : 'text-silver group-hover:text-platinum'
            }`}
          >
            {item.question}
          </span>
          <span
            aria-hidden="true"
            className={`grid size-9 shrink-0 place-items-center rounded-full border transition duration-300 ${
              open
                ? 'rotate-45 border-lime bg-lime text-void'
                : 'border-line bg-raised text-steel group-hover:border-lime/40'
            }`}
          >
            <Plus className="size-4" strokeWidth={1.8} />
          </span>
        </button>
      </h3>

      <div
        ref={panelRef}
        id={`${id}-panel`}
        role="region"
        aria-labelledby={`${id}-button`}
        className="overflow-hidden"
      >
        <div className="max-w-[68ch] pb-7 text-[15px] leading-relaxed text-steel">
          {item.answer}
        </div>
      </div>
    </div>
  )
}

function Accordion({
  items,
  defaultOpenId,
  allowMultiple = false,
  className = '',
}: AccordionProps) {
  /* Null is "nobody has touched this yet", which is a different thing from
     "everything is closed" — and keeping the difference is what lets the
     default follow the questions.

     It has to follow them because the list can change once under the reader:
     the first paint draws the built-in answers and the console's own arrive a
     moment later under different ids. Seeded into an array up front, the open
     panel would be one that no longer exists and the accordion would come to
     rest with nothing open at all. Until somebody clicks, "open" is simply
     asked of the default rather than remembered. */
  const [openIds, setOpenIds] = useState<string[] | null>(null)

  const isOpen = (id: string) => (openIds === null ? id === defaultOpenId : openIds.includes(id))

  const toggle = (id: string) =>
    setOpenIds((current) => {
      const open = current ?? (defaultOpenId ? [defaultOpenId] : [])
      if (open.includes(id)) return open.filter((other) => other !== id)
      return allowMultiple ? [...open, id] : [id]
    })

  return (
    <div className={className}>
      {items.map((item) => (
        <AccordionRow
          key={item.id}
          item={item}
          open={isOpen(item.id)}
          onToggle={() => toggle(item.id)}
        />
      ))}
    </div>
  )
}

export default Accordion
